// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { FormDatePicker } from '@/components/form-date-picker';
import { MAXIMUM_DATE, MINIMUM_DATE } from '@/constants/date-shortcut';
import { InfoOutlined } from '@/constants/hardfin-standard-icons';
import {
  BILLING_PLAN_INPUT_WIDTH,
  BillingPlanFormValues,
  TOGGLE_BUTTON_GROUP_SX,
  TOGGLE_BUTTON_SX,
} from '@/project/components/billing-plan-constants';
import {
  Box,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormClearErrors,
  useWatch,
} from 'react-hook-form';

interface Props {
  control: Control<BillingPlanFormValues>;
  hasDates: boolean;
  // True once any charge is BILLABLE or INVOICED. The toggle and date
  // input lock so the anchor cannot move while real billing rows depend
  // on it.
  hasLockedCharges: boolean;
  // Milestone plans render only the secondary-text placeholder — phases
  // activate individually via mark-billable, so the toggle and date
  // input do not apply.
  isMilestone: boolean;
  errors: FieldErrors<BillingPlanFormValues>;
  clearErrors: UseFormClearErrors<BillingPlanFormValues>;
}

export function BillingPlanActivationSection(props: Props) {
  // Hooks must run unconditionally; gate the visible output below.
  const chargeMode = useWatch({
    control: props.control,
    name: 'chargeMode',
  });

  if (!props.hasDates) {
    return null;
  }

  // Milestone plans don't have a single activation anchor — each phase
  // activates individually via mark-billable. The card renders a single
  // secondary-text line in place of the toggle / date input so the
  // drawer structure stays consistent across schedule types.
  if (props.isMilestone) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
          Activation date
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Activation date is determined by milestone achievement
        </Typography>
      </Paper>
    );
  }

  const lockedTooltip =
    'Cannot change activation while billable or invoiced charges exist';
  const lockedTooltipTitle = props.hasLockedCharges ? lockedTooltip : '';
  // PENDING plans hide the date input — there's no real anchor to
  // display until the user flips back to CONFIRMED.
  const showActivationDate = chargeMode !== 'PENDING';

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
        Activation date
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip arrow title={lockedTooltipTitle}>
            <Box>
              <Controller
                control={props.control}
                name="chargeMode"
                render={(renderProps) => (
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    sx={TOGGLE_BUTTON_GROUP_SX}
                    value={renderProps.field.value}
                    disabled={props.hasLockedCharges}
                    onChange={(_event, value) => {
                      if (value !== null) {
                        renderProps.field.onChange(value);
                        if (value === 'PENDING') {
                          props.clearErrors('activationDate');
                        }
                      }
                    }}
                  >
                    <ToggleButton value="CONFIRMED" sx={TOGGLE_BUTTON_SX}>
                      Confirmed
                    </ToggleButton>
                    <ToggleButton value="PENDING" sx={TOGGLE_BUTTON_SX}>
                      Pending
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}
              />
            </Box>
          </Tooltip>
          <Tooltip
            arrow
            title={
              <Box>
                <Typography variant="caption">
                  <b>Confirmed</b>: Charges can be scheduled now with specific
                  billable dates based on the activation date (e.g., contract is
                  signed and service start date is known).
                </Typography>
                <Box sx={{ mt: 1 }} />
                <Typography variant="caption">
                  <b>Pending</b>: The activation date is not yet confirmed, so
                  charges will be scheduled from the activation date once it is
                  confirmed (e.g., still awaiting a shipment or deployment date
                  for the line item).
                </Typography>
              </Box>
            }
          >
            <InfoOutlined
              sx={{
                fontSize: '1rem',
                color: 'text.secondary',
                cursor: 'help',
              }}
            />
          </Tooltip>
        </Box>
        {showActivationDate ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip arrow title={lockedTooltipTitle}>
              <Box sx={{ width: BILLING_PLAN_INPUT_WIDTH }}>
                <FormDatePicker
                  name="activationDate"
                  control={props.control}
                  isDisabled={props.hasLockedCharges}
                  minimumDate={MINIMUM_DATE}
                  maximumDate={MAXIMUM_DATE}
                  onChangeComplete={(date) => {
                    if (date !== null && props.errors.activationDate) {
                      props.clearErrors('activationDate');
                    }
                  }}
                />
              </Box>
            </Tooltip>
            <Tooltip
              arrow
              title={
                <Box>
                  <Typography variant="caption" fontWeight={600}>
                    Activation date:
                  </Typography>
                  <Typography variant="caption">
                    {' '}
                    the anchor charge generation uses to start the schedule.
                    Defaults to the line item start. Changing it shifts the
                    whole schedule by the same number of days, preserving the
                    total duration.
                  </Typography>
                </Box>
              }
            >
              <InfoOutlined
                sx={{
                  fontSize: '1rem',
                  color: 'text.secondary',
                  cursor: 'help',
                }}
              />
            </Tooltip>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Not yet confirmed
          </Typography>
        )}
      </Box>
    </Paper>
  );
}
